import React, { createContext, useContext, useReducer, useCallback } from 'react';

const ModalContext = createContext();

// Modal state reducer
const modalReducer = (state, action) => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return {
        ...state,
        [action.modalName]: {
          isOpen: true,
          props: action.props || {},
        },
      };

    case 'CLOSE_MODAL':
      return {
        ...state,
        [action.modalName]: {
          isOpen: false,
          props: {},
        },
      };

    case 'CLOSE_ALL_MODALS':
      const closedState = {};
      Object.keys(state).forEach(key => {
        closedState[key] = { isOpen: false, props: {} };
      });
      return closedState;

    case 'TOGGLE_MODAL':
      return {
        ...state,
        [action.modalName]: {
          isOpen: !state[action.modalName]?.isOpen,
          props: action.props || state[action.modalName]?.props || {},
        },
      };

    default:
      return state;
  }
};

// Initial modal state - all modals closed
const initialModalState = {
  // Core modals
  wantlistRelease: { isOpen: false, props: {} },
  wantedReleases: { isOpen: false, props: {} },
  wantlist: { isOpen: false, props: {} },
  addMaster: { isOpen: false, props: {} },
  addToCollection: { isOpen: false, props: {} },
  movieSearch: { isOpen: false, props: {} },
  movieDetail: { isOpen: false, props: {} },
  profileEdit: { isOpen: false, props: {} },
  profileCompletion: { isOpen: false, props: {} },

  // Scanner/Upload modals
  upcScanner: { isOpen: false, props: {} },
  scannedMovieForm: { isOpen: false, props: {} },

  // Image modals
  imageModal: { isOpen: false, props: {} },
  releaseImageUpload: { isOpen: false, props: {} },
  releaseCamera: { isOpen: false, props: {} },
};

export const ModalProvider = ({ children }) => {
  const [modalState, dispatch] = useReducer(modalReducer, initialModalState);

  // Core modal actions
  const openModal = useCallback((modalName, props = {}) => {
    dispatch({ type: 'OPEN_MODAL', modalName, props });
  }, []);

  const closeModal = useCallback(modalName => {
    dispatch({ type: 'CLOSE_MODAL', modalName });
  }, []);

  const toggleModal = useCallback((modalName, props = {}) => {
    dispatch({ type: 'TOGGLE_MODAL', modalName, props });
  }, []);

  const closeAllModals = useCallback(() => {
    dispatch({ type: 'CLOSE_ALL_MODALS' });
  }, []);

  // Convenience functions for checking modal state
  const isModalOpen = useCallback(
    modalName => {
      return modalState[modalName]?.isOpen || false;
    },
    [modalState]
  );

  const getModalProps = useCallback(
    modalName => {
      return modalState[modalName]?.props || {};
    },
    [modalState]
  );

  // Convenience functions for common modal operations
  const openWantlistModal = useCallback(
    (movie, userWantlist = []) => {
      openModal('wantlist', { movie, userWantlist, mode: 'unified' });
    },
    [openModal]
  );

  const openWantedReleasesModal = useCallback(
    (movie, userWantlist) => {
      openModal('wantedReleases', { movie, userWantlist });
    },
    [openModal]
  );

  const openAddMasterModal = useCallback(
    (editingItem = null, isDataFromSearch = false) => {
      openModal('addMaster', { editingItem, isDataFromSearch });
    },
    [openModal]
  );

  const openAddToCollectionModal = useCallback(
    masterItem => {
      openModal('addToCollection', { masterItem });
    },
    [openModal]
  );

  const openUnifiedCollectionModal = useCallback(
    (movie, userCollectionItems = []) => {
      openModal('addToCollection', {
        masterItem: movie,
        movie: movie,
        mode: 'unified',
        userCollectionItems,
      });
    },
    [openModal]
  );

  const openMovieSearchModal = useCallback(
    (initialSearchTerm = '', showSkipButton = false) => {
      openModal('movieSearch', { initialSearchTerm, showSkipButton });
    },
    [openModal]
  );

  const openImageModal = useCallback(
    (imageUrl, title = '') => {
      openModal('imageModal', { imageUrl, title });
    },
    [openModal]
  );

  const openUPCScanner = useCallback(() => {
    openModal('upcScanner');
  }, [openModal]);

  const openProfileEdit = useCallback(() => {
    openModal('profileEdit');
  }, [openModal]);

  const openProfileCompletion = useCallback(() => {
    openModal('profileCompletion');
  }, [openModal]);

  // Global ESC key handler
  React.useEffect(() => {
    const handleGlobalEscape = e => {
      if (e.key === 'Escape') {
        // Find the most recently opened modal and close it
        const openModals = Object.keys(modalState).filter(key => modalState[key].isOpen);
        if (openModals.length > 0) {
          // Close the last opened modal (simple LIFO approach)
          closeModal(openModals[openModals.length - 1]);
        }
      }
    };

    document.addEventListener('keydown', handleGlobalEscape);
    return () => document.removeEventListener('keydown', handleGlobalEscape);
  }, [modalState, closeModal]);

  const value = {
    // State
    modalState,

    // Core actions
    openModal,
    closeModal,
    toggleModal,
    closeAllModals,

    // State checkers
    isModalOpen,
    getModalProps,

    // Convenience functions
    openWantlistModal,
    openWantedReleasesModal,
    openAddMasterModal,
    openAddToCollectionModal,
    openUnifiedCollectionModal,
    openMovieSearchModal,
    openImageModal,
    openUPCScanner,
    openProfileEdit,
    openProfileCompletion,
  };

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

// Export modal names as constants for type safety
export const MODAL_NAMES = {
  WANTLIST_RELEASE: 'wantlistRelease',
  WANTED_RELEASES: 'wantedReleases',
  WANTLIST: 'wantlist',
  ADD_MASTER: 'addMaster',
  ADD_TO_COLLECTION: 'addToCollection',
  MOVIE_SEARCH: 'movieSearch',
  MOVIE_DETAIL: 'movieDetail',
  PROFILE_EDIT: 'profileEdit',
  PROFILE_COMPLETION: 'profileCompletion',
  UPC_SCANNER: 'upcScanner',
  SCANNED_MOVIE_FORM: 'scannedMovieForm',
  IMAGE_MODAL: 'imageModal',
  RELEASE_IMAGE_UPLOAD: 'releaseImageUpload',
  RELEASE_CAMERA: 'releaseCamera',
};
